// Prepayments: cash received before any invoice exists. Recording posts
// DR bank / CR deferred revenue (a liability — never revenue); applying
// moves part of it against a later invoice (DR deferred / CR AR) without
// manufacturing a payment row, since no cash moves there.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { PeriodLockWarning } from "@/components/PeriodLockWarning";
import { LinkedContactPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import { ActionMenu } from "@/components/ActionMenu";
import { StatusFilter } from "@/components/StatusFilter";
import {
  clearDocumentDraft,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import {
  getPeriodLock,
  listPrepayments,
  type Prepayment,
  recordPrepayment,
} from "@/lib/api";
import { humanizeLabel } from "@/lib/labels";
import { useViewSearch } from "@/lib/view-search";
import { useOrganization } from "@/lib/organization";

const PREPAYMENT_DRAFT_PREFIX = "niche-accounting.prepayment-draft.v1";
const PREPAYMENT_STATUS_FILTERS = [
  "received",
  "partially_applied",
  "applied",
  "void",
] as const;

function prepaymentStatusFilter(
  value: string,
): "" | (typeof PREPAYMENT_STATUS_FILTERS)[number] {
  return (PREPAYMENT_STATUS_FILTERS as readonly string[]).includes(value)
    ? (value as (typeof PREPAYMENT_STATUS_FILTERS)[number])
    : "";
}

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "applied":
      return "success";
    case "partially_applied":
      return "info";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  return status
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function prepaymentCurrency(currency: string | undefined): string {
  return currency || "USD";
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function PrepaymentsPage() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  const [prepayments, setPrepayments] = useState<Prepayment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank_transfer");

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/sort params — filtering happens here). Status stays in the URL.
  const [statusTab, setStatusTab] = useViewSearch<
    "" | (typeof PREPAYMENT_STATUS_FILTERS)[number]
  >("status", PREPAYMENT_STATUS_FILTERS, "");
  const [search, setSearch] = useState("");

  // Draft mirror: load from this browser session when the organization is known,
  // then save on every change. A failed storage read yields a fresh draft.
  // The mirror only carries the customer name, linked contact and received
  // date (kept in `dueAt`); amount and method stay in this page's state.
  useEffect(() => {
    const stored = loadDocumentDraft(PREPAYMENT_DRAFT_PREFIX, organizationId);
    const next = stored ?? newDocumentDraft();
    if (!next.dueAt) next.dueAt = todayInput();
    setDraft(next);
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(PREPAYMENT_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  const reload = () => {
    setError(null);
    listPrepayments()
      .then(setPrepayments)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  };

  useEffect(reload, []);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const clearDraft = () => {
    const fresh = newDocumentDraft();
    fresh.dueAt = todayInput();
    setDraft(fresh);
    setAmount("");
    setMethod("bank_transfer");
    clearDocumentDraft(PREPAYMENT_DRAFT_PREFIX, organizationId);
  };

  const hasDraft =
    draft.partyName.trim() !== "" ||
    draft.crmContactId !== "" ||
    amount.trim() !== "" ||
    method.trim() !== "bank_transfer";

  const statusCounts = (prepayments ?? []).reduce<Record<string, number>>(
    (counts, prepayment) => {
      const key = prepayment.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  // Client-side filter over the fetched list.
  const visiblePrepayments = (prepayments ?? [])
    .filter(
      (prepayment) =>
        !statusTab || prepayment.status.toLowerCase() === statusTab,
    )
    .filter((prepayment) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        prepayment.customer_name.toLowerCase().includes(query) ||
        prepayment.method.toLowerCase().includes(query)
      );
    });

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Prepayments"
        description="Money received before invoicing — held aside and applied to later invoices."
        actions={
          <Button type="button" onClick={() => setDrawer(true)}>
            New prepayment
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      <StatusFilter
        label="Filter prepayments"
        value={statusTab}
        onChange={(value) => setStatusTab(prepaymentStatusFilter(value))}
        options={[
          { value: "", label: "All", count: (prepayments ?? []).length },
          { value: "received", label: "Received", count: statusCounts.received ?? 0 },
          { value: "partially_applied", label: "Partially applied", count: statusCounts.partially_applied ?? 0 },
          { value: "applied", label: "Applied", count: statusCounts.applied ?? 0 },
          { value: "void", label: "Void", count: statusCounts.void ?? 0 },
        ]}
      />
        <div className="surface grid gap-3 rounded-2xl p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label
              htmlFor="prepayments-search"
              className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
            >
              Search
              <Input
                id="prepayments-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Customer or method…"
              />
            </label>
            <Button type="button" variant="secondary" onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>

        <div
          className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
          data-testid="prepayments-table"
        >
          <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Still available</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prepayments === null ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {error ? "Could not load prepayments." : "Loading…"}
                    </TableCell>
                  </TableRow>
                ) : prepayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No prepayments yet — record the first one above.
                    </TableCell>
                  </TableRow>
                ) : visiblePrepayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      No prepayments match these filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  visiblePrepayments.map((prepayment) => (
                    <TableRow key={prepayment.id}>
                      <TableCell>
                        <Link
                          to="/prepayments/$prepaymentId"
                          params={{ prepaymentId: prepayment.id }}
                          className="font-medium hover:underline"
                        >
                          {prepayment.customer_name}
                        </Link>
                        <span className="block text-xs text-[var(--muted-foreground)]">
                          {humanizeLabel(prepayment.method)}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {new Date(prepayment.received_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <StatusDot
                          tone={statusTone(prepayment.status)}
                          label={plainStatus(prepayment.status)}
                        />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: prepayment.amount_minor,
                          currency: prepaymentCurrency(prepayment.currency_code),
                        })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney({
                          cents: prepayment.unapplied_minor,
                          currency: prepaymentCurrency(prepayment.currency_code),
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <ActionMenu
                            label="Actions"
                            ariaLabel={`Actions for ${prepayment.customer_name}`}
                            items={[
                              {
                                id: "open",
                                label: "Open prepayment",
                                to: "/prepayments/$prepaymentId",
                                params: { prepaymentId: prepayment.id },
                              },
                            ]}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div
          className="stack-list grid gap-3 min-[64rem]:hidden"
          data-testid="prepayments-stack"
        >
          {prepayments === null ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              {error ? "Could not load prepayments." : "Loading…"}
            </p>
          ) : prepayments.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              No prepayments yet — record the first one.
            </p>
          ) : visiblePrepayments.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              No prepayments match these filters.
            </p>
          ) : (
            visiblePrepayments.map((prepayment) => (
              <article
                key={prepayment.id}
                className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
              >
                <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                  <Link
                    to="/prepayments/$prepaymentId"
                    params={{ prepaymentId: prepayment.id }}
                    className="record-title underline-offset-4 hover:underline"
                  >
                    {prepayment.customer_name}
                  </Link>
                  <span className="tabular-nums">
                    {formatMoney({
                      cents: prepayment.amount_minor,
                      currency: prepaymentCurrency(prepayment.currency_code),
                    })}
                  </span>
                </div>
                <CardField label="Method">
                  {humanizeLabel(prepayment.method)}
                </CardField>
                <CardField label="Received">
                  {new Date(prepayment.received_at).toLocaleDateString()}
                </CardField>
                <CardField label="Status">
                  <StatusDot
                    tone={statusTone(prepayment.status)}
                    label={plainStatus(prepayment.status)}
                  />
                </CardField>
                <CardField label="Amount">
                  {formatMoney({
                    cents: prepayment.amount_minor,
                    currency: prepaymentCurrency(prepayment.currency_code),
                  })}
                </CardField>
                <CardField label="Still available">
                  {formatMoney({
                    cents: prepayment.unapplied_minor,
                    currency: prepaymentCurrency(prepayment.currency_code),
                  })}
                </CardField>
                <div className="flex flex-wrap justify-end pt-3">
                  <ActionMenu
                    label="Actions"
                    ariaLabel={`Actions for ${prepayment.customer_name}`}
                    items={[
                      {
                        id: "open",
                        label: "Open prepayment",
                        to: "/prepayments/$prepaymentId",
                        params: { prepaymentId: prepayment.id },
                      },
                    ]}
                  />
                </div>
              </article>
            ))
          )}
        </div>

      <RecordPrepaymentDrawer
        open={drawer}
        draft={draft}
        onDraftChange={updateDraft}
        amount={amount}
        onAmountChange={setAmount}
        method={method}
        onMethodChange={setMethod}
        hasDraft={hasDraft}
        onDiscard={clearDraft}
        onClose={() => setDrawer(false)}
        onDone={() => {
          reload();
          clearDraft();
          setDrawer(false);
        }}
      />
    </div>
  );
}

function RecordPrepaymentDrawer({
  open,
  draft,
  onDraftChange,
  amount,
  onAmountChange,
  method,
  onMethodChange,
  hasDraft,
  onDiscard,
  onClose,
  onDone,
}: {
  open: boolean;
  draft: DocumentDraft;
  onDraftChange: (patch: Partial<DocumentDraft>) => void;
  amount: string;
  onAmountChange: (value: string) => void;
  method: string;
  onMethodChange: (value: string) => void;
  hasDraft: boolean;
  onDiscard: () => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [lockDate, setLockDate] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPeriodLock()
      .then((lock) => setLockDate(lock.lock_date ?? lock.advisor_lock_date))
      .catch(() => setLockDate(undefined));
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const name = draft.partyName.trim();
      if (!name) throw new Error("Customer name is required.");
      const amount_minor = toMinor(amount);
      if (amount_minor === null)
        throw new Error("Amount must be a positive number (e.g. 12.50).");
      if (!method.trim()) throw new Error("Payment method is required.");
      if (!draft.dueAt) throw new Error("Received date is required.");
      const created = await recordPrepayment({
        customer_name: name,
        ...(draft.crmContactId ? { crm_contact_id: draft.crmContactId } : {}),
        amount_minor,
        method: method.trim(),
        received_at: new Date(`${draft.dueAt}T00:00:00`).toISOString(),
      });
      onDone();
      void navigate({
        to: "/prepayments/$prepaymentId",
        params: { prepaymentId: created.id },
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Record a prepayment"
      description="Books cash received before invoicing as deferred revenue (a liability — it never counts as income until applied)."
      className="w-[min(34rem,100%)]"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Back to prepayments
          </Button>
          {hasDraft ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onDiscard}
              disabled={saving}
            >
              Discard draft
            </Button>
          ) : null}
          <Button
            type="submit"
            form="prepayment-create-form"
            disabled={saving}
            className="ms-auto"
          >
            {saving ? "Recording…" : "Record prepayment"}
          </Button>
        </>
      }
    >
      <form
        id="prepayment-create-form"
        onSubmit={submit}
        className="grid gap-3"
        aria-label="Record a prepayment"
      >
        <PeriodLockWarning lockDate={lockDate} chosenDate={draft.dueAt} />
        {error ? (
          <p role="alert" className="text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <label
          htmlFor="prep-customer"
          className="grid gap-1 text-xs font-medium"
        >
          Customer name
          <Input
            id="prep-customer"
            value={draft.partyName}
            onChange={(e) => onDraftChange({ partyName: e.target.value })}
            required
            placeholder="Acme Co."
            disabled={saving}
          />
        </label>
        <div className="grid gap-1 text-xs font-medium">
          <span id="prep-contact-label">Linked contact (optional)</span>
          <LinkedContactPicker
            id="prep-contact"
            value={draft.crmContactId}
            onChange={(value) => onDraftChange({ crmContactId: value })}
            disabled={saving}
          />
        </div>
        <label htmlFor="prep-amount" className="grid gap-1 text-xs font-medium">
          Amount (USD)
          <Input
            id="prep-amount"
            type="number"
            value={amount}
            onChange={(e) => onAmountChange(e.target.value)}
            required
            placeholder="0.00"
            step="0.01"
            min="0"
            inputMode="decimal"
            disabled={saving}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label
            htmlFor="prep-method"
            className="grid gap-1 text-xs font-medium"
          >
            Method
            <Input
              id="prep-method"
              value={method}
              onChange={(e) => onMethodChange(e.target.value)}
              required
              placeholder="bank_transfer"
              disabled={saving}
            />
          </label>
          <label htmlFor="prep-date" className="grid gap-1 text-xs font-medium">
            Received on
            <Input
              id="prep-date"
              type="date"
              value={draft.dueAt}
              onChange={(e) => onDraftChange({ dueAt: e.target.value })}
              required
              disabled={saving}
            />
          </label>
        </div>
      </form>
    </Drawer>
  );
}
